import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, IconButton, Typography, Divider, Grid, Chip, Card, MenuItem, Button, Select, FormControl, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Snackbar, Alert } from "@mui/material";
import AppDialog from "../../../../common/AppDialog";
import { ArrowBack, Assignment, Edit, Delete, Groups, Person, CalendarMonth, Timer, CheckCircleOutline, Description, Add, AccountTree, Visibility, Flag, DonutLarge, Save, Close } from '@mui/icons-material';
import { useNavigate, useSearchParams } from "react-router-dom";
import { getData, putData, deleteData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';
import Loader from '../../../../common/spinner';
import CreateTask from '../taskManagement/createTask';
import { getStatusColor, priorityColors } from '../../../../utils/statusColor';

function UserStoryDetails() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userRole = userData?.user_type;
    const roleBasePath = userRole === 'Team Lead' ? '/team-lead' : '/manager';
    const storyId = searchParams.get("storyId");

    const [state, setState] = useState({
        loading: true,
        data: null,
        error: null,
        isEditing: false,
        isUpdating: false,
        isDeleting: false
    });

    const [dialogs, setDialogs] = useState({
        deleteStory: false,
        createTask: false,
        taskToDelete: null,
        isTaskDeleting: false
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const [formData, setFormData] = useState({});
    const [pagination, setPagination] = useState({ page: 0, rowsPerPage: 5 });

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchDetails = useCallback(async () => {
        try {
            setState(prev => ({ ...prev, loading: true }));
            const res = await getData(API_ENDPOINTS.fetchUserStoryDetails(storyId));
            const data = res.data || res;

            if (data.status === "SUCCESS") {
                const story = { ...data.result.story, tasks: data.result.tasks || [] };
                setState(prev => ({ ...prev, data: story, loading: false }));
                setFormData({
                    title: story.title,
                    description: story.description,
                    priority: story.priority,
                    status: story.status,
                    story_points: story.story_points,
                    acceptance_criteria: story.acceptance_criteria
                });
            } else {
                setState(prev => ({ ...prev, error: "Failed to load story details", loading: false }));
            }
        } catch (err) {
            setState(prev => ({ ...prev, error: err.message, loading: false }));
        }
    }, [storyId]);

    useEffect(() => { storyId && fetchDetails(); }, [storyId, fetchDetails]);

    const handleUpdateStory = async () => {
        try {
            setState(p => ({ ...p, isUpdating: true }));
            const res = await putData(API_ENDPOINTS.updateUserStoryById(storyId), formData);
            if (res.status === "SUCCESS" || res.status === 200) {
                showSnackbar("Updated successfully");
                setState(p => ({ ...p, isEditing: false }));
                fetchDetails();
            }
        } catch (err) {
            showSnackbar(err.message, "error");
        } finally {
            setState(p => ({ ...p, isUpdating: false }));
        }
    };

    const handleDeleteAction = async (type) => {
        if (type === 'Story') {
            try {
                setState(p => ({ ...p, isDeleting: true }));
                const res = await deleteData(API_ENDPOINTS.deleteUserStoryById(storyId));
                if (res.status === "SUCCESS" || res.status === 200) {
                    showSnackbar("Story deleted successfully");
                    navigate(-1);
                }
            } catch (err) {
                showSnackbar(err.message, "error");
            } finally {
                setState(p => ({ ...p, isDeleting: false }));
                setDialogs(p => ({ ...p, deleteStory: false }));
            }
        } else if (type === 'SubTask') {
            const taskId = dialogs.taskToDelete?.id || dialogs.taskToDelete?.task_id;

            if (!taskId) {
                showSnackbar("Invalid Task ID", "error");
                return;
            }

            try {
                setDialogs(p => ({ ...p, isTaskDeleting: true }));

                // Ensure API_ENDPOINTS.deleteTaskById(taskId) returns the correct URL
                const res = await deleteData(API_ENDPOINTS.deleteUserTaskById(taskId));

                if (res.status === "SUCCESS" || res.status === 200) {
                    showSnackbar("Task deleted successfully");
                    setDialogs(p => ({ ...p, taskToDelete: null }));
                    fetchDetails(); // Refresh the list
                } else {
                    showSnackbar(res.message || "Deletion failed", "error");
                }
            } catch (err) {
                showSnackbar(err.response?.data?.message || err.message, "error");
            } finally {
                setDialogs(p => ({ ...p, isTaskDeleting: false }));
            }
        }
    };

    if (state.loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Loader /></Box>;
    if (state.error || !state.data) return <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="error">{state.error || "Not found"}</Typography><Button onClick={() => navigate(-1)}>Back</Button></Box>;

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const metaItems = [
        { label: 'Project', val: state.data.project_title, icon: <AccountTree fontSize="small" /> },
        { label: 'Epic', val: state.data.epic_title || 'No Epic Linked', icon: <Assignment fontSize="small" /> },
        { label: 'Product Owner', val: state.data.product_owner_name || 'Unassigned', icon: <Person fontSize="small" /> },
        { label: 'Team Lead', val: state.data.team_lead_name || 'Unassigned', icon: <Groups fontSize="small" /> },
        { label: 'Timeline', val: `${formatDate(state.data.start_date)} - ${formatDate(state.data.due_date)}`, icon: <CalendarMonth fontSize="small" /> },
        {
            label: 'Story Points',
            val: state.data.story_points,
            icon: <span style={{ fontWeight: 'bold' }}>#</span>,
            isEditable: true,
            name: 'story_points',
            options: [1, 2, 3, 5, 8, 13, 21, 34]
        },
        { label: 'Estimated Time', val: `${state.data.allocated_hours || 0} hrs`, icon: <Timer fontSize="small" /> },
        { label: 'Actual Time', val: `${state.data.actual_time || 0} hrs`, icon: <CheckCircleOutline fontSize="small" />, color: '#10b981' },
        {
            label: 'Priority',
            val: state.data.priority,
            icon: <Flag fontSize="small" />,
            isChip: true,
            isEditable: true,
            name: 'priority',
            options: ['Low', 'Medium', 'High'],
            colors: priorityColors[state.data.priority] || { bg: '#f1f5f9', text: '#64748b' }
        },
        {
            label: 'Status',
            val: state.data.status,
            icon: <DonutLarge fontSize="small" />,
            isChip: true,
            isEditable: true,
            name: 'status',
            options: ['Backlog', 'Ready for Dev', 'In Development', 'Completed'],
            colors: getStatusColor(state.data.status) || { bg: '#f1f5f9', text: '#64748b' }
        },
    ];

    return (
        <>
            {/* Header Section */}
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', px: 2 }}>
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ color: '#64748b', borderRadius: '0px', p: 1, '&:hover': { bgcolor: '#e0e3e6' } }}
                >
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500, color: '#475569' }}>Back</Typography>
                </IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, alignSelf: 'center', background: '#e2e8f0' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexGrow: 1, py: 1 }}>
                    <Typography variant="h6" fontWeight="bold">User Story Details</Typography>
                    {state.isEditing && (
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={state.isUpdating ? <Loader size={16} color="inherit" /> : <Save />}
                                onClick={handleUpdateStory}
                                sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                            >
                                Save
                            </Button>
                            <Button variant="outlined" size="small" onClick={() => setState(p => ({ ...p, isEditing: false }))}>Cancel</Button>
                        </Stack>
                    )}
                </Box>
            </Stack>

            <Box sx={{ p: 2 }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                        {/* Title & Actions */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" fontWeight={600}><span style={{ color: '#3b82f6' }}>#{state.data.story_code}</span> {state.data.title}</Typography>
                            {!state.isEditing && (
                                <Stack direction="row">
                                    <IconButton onClick={() => setState(p => ({ ...p, isEditing: true }))}><Edit sx={{ color: '#f59e0b' }} /></IconButton>
                                    <IconButton onClick={() => setDialogs(p => ({ ...p, deleteStory: true }))}><Delete sx={{ color: '#ef4444' }} /></IconButton>
                                </Stack>
                            )}
                        </Box>

                        {/* Meta Grid */}
                        <Grid container spacing={2}>
                            {metaItems.map((item, i) => (
                                <Grid size={{ xs: 12, sm: 6 }} key={i}>
                                    <Stack
                                        direction="row"
                                        spacing={0.5}
                                        alignItems="center" // Keeps everything vertically centered
                                        sx={{ minHeight: 24 }} // Ensures consistent height across rows
                                    >
                                        {/* Icon Container with fixed width to ensure labels start at the same point */}
                                        <Box sx={{
                                            color: item.isChip ? item.colors?.text : '#94a3b8',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 24, // Fixed width for the icon area
                                        }}>
                                            {item.icon}
                                        </Box>

                                        {/* Label with a consistent width */}
                                        <Typography
                                            variant="body2"
                                            color="#64748b"
                                            sx={{ width: 110, flexShrink: 0 }}
                                        >
                                            {item.label}
                                        </Typography>

                                        {/* Value / Control Area */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                            {state.isEditing && item.isEditable ? (
                                                <FormControl size="small" fullWidth={false} sx={{ minWidth: 120 }}>
                                                    <Select
                                                        name={item.name}
                                                        value={formData[item.name] || ''}
                                                        onChange={(e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }))}
                                                        sx={{
                                                            height: 32,
                                                            fontSize: '0.875rem',
                                                            '& .MuiSelect-select': { py: 0.5 }
                                                        }}
                                                    >
                                                        {item.options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            ) : item.isChip ? (
                                                <Chip
                                                    label={item.val}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: item.colors?.bg,
                                                        color: item.colors?.color,
                                                        fontWeight: 600,
                                                        px: 1,
                                                        border: `1px solid ${item.colors?.border}`
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="body2" fontWeight={600} sx={{ color: item.color || 'inherit' }}>
                                                    {item.val}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Description & Acceptance Criteria */}
                        {['Description', 'Acceptance Criteria'].map((label, i) => (
                            <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }} key={label}>
                                <Box sx={{ p: 1.5, bgcolor: i === 0 ? '#f0f9ff' : '#f0fdf4', display: 'flex', gap: 1 }}>
                                    {i === 0 ? <Description sx={{ color: '#0369a1', fontSize: 20 }} /> : <CheckCircleOutline sx={{ color: '#15803d', fontSize: 20 }} />}
                                    <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
                                </Box>
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#334155' }}>
                                        {i === 0 ? state.data.description : state.data.acceptance_criteria}
                                    </Typography>
                                </Box>
                            </Card>
                        ))}

                        {/* Tasks Table Section */}
                        <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
                            <Box sx={{ p: 1.5, bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
                                <Typography variant="subtitle2" fontWeight={700}>Tasks ({state.data.tasks.length}) — {state.data.completed_tasks || 0} completed</Typography>
                                <Button size="small" startIcon={<Add />} onClick={() => setDialogs(p => ({ ...p, createTask: true }))} variant="contained" sx={{ textTransform: 'none', borderRadius: '6px', bgcolor: 'transparent', color: '#2563eb', border: '1px solid #2563eb' }}>Add Task</Button>
                            </Box>
                            {state.data.tasks.length > 0 ? (
                                <>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                                <TableRow>
                                                    {['Task Name', 'Task Type', 'Work Type', 'Assignee', 'Est.', 'Actual', 'Status', 'Actions'].map(h => (
                                                        <TableCell key={h} sx={{ fontWeight: 700, color: '#475569' }}>{h}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {state.data.tasks.slice(pagination.page * pagination.rowsPerPage, pagination.page * pagination.rowsPerPage + pagination.rowsPerPage).map((t, i) => {
                                                    // Get dynamic colors for the specific task status
                                                    const statusStyle = getStatusColor(t.status);

                                                    return (
                                                        <TableRow key={i} hover>
                                                            <TableCell sx={{ fontWeight: 500 }}>{t.task_title}</TableCell>
                                                            <TableCell><Chip label={t.task_type || 'Dev'} size="small" sx={{ fontSize: '0.7rem' }} /></TableCell>
                                                            <TableCell><Chip label={t.work_type || 'Dev'} size="small" sx={{ fontSize: '0.7rem' }} /></TableCell>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={-1}>
                                                                    {t.assigned_users?.map((u, j) => (
                                                                        <Tooltip key={j} title={u.name}>
                                                                            <Box sx={{ width: 28, height: 28, bgcolor: '#3b82f6', color: '#fff', borderRadius: '50%', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 'bold' }}>
                                                                                {u.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                                                                            </Box>
                                                                        </Tooltip>
                                                                    ))}
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>{t.estimated_hours}h</TableCell>
                                                            <TableCell>{t.actual_hours}h</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={t.status}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: statusStyle.bg,
                                                                        color: statusStyle.color,
                                                                        fontWeight: 700,
                                                                        px: 1,
                                                                        border: statusStyle.border || '1px solid #cbd5e1'
                                                                    }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={0.5}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            navigate(`${roleBasePath}/user-task-details?taskId=${t.id}`);
                                                                        }}
                                                                    >
                                                                        <Visibility sx={{ fontSize: 18, color: '#64748b' }} />
                                                                    </IconButton>
                                                                    <IconButton size="small" onClick={() => setDialogs(p => ({ ...p, taskToDelete: t }))}>
                                                                        <Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                                                                    </IconButton>
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        component="div"
                                        count={state.data.tasks.length}
                                        {...pagination}
                                        rowsPerPageOptions={[5, 10]}
                                        onPageChange={(_, p) => setPagination(prev => ({ ...prev, page: p }))}
                                        onRowsPerPageChange={(e) => setPagination({ page: 0, rowsPerPage: parseInt(e.target.value, 10) })}
                                    />
                                </>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: 6 }}>
                                    <Typography color="textSecondary">No tasks created yet for this story.</Typography>
                                </Box>
                            )}
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* 1. DELETE USER STORY DIALOG (Main Story) */}
            <AppDialog
                open={dialogs.deleteStory}
                onClose={() => setDialogs(p => ({ ...p, deleteStory: false }))}
                PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}
            >
                <DialogTitle>Delete User Story?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <b>{state.data.title}</b>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogs(p => ({ ...p, deleteStory: false }))} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        sx={{ bgcolor: '#ef4444', textTransform: 'none', '&:hover': { bgcolor: '#dc2626' } }}
                        onClick={() => handleDeleteAction('Story')}
                    >
                        {state.isDeleting ? <Loader size={18} color="inherit" /> : 'Delete Story'}
                    </Button>
                </DialogActions>
            </AppDialog>

            {/* 2. DELETE TASK DIALOG (Sub-task) */}
            <AppDialog
                open={Boolean(dialogs.taskToDelete)}
                onClose={() => setDialogs(p => ({ ...p, taskToDelete: null }))}
                PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}
                maxWidth="sm"
            >
                <DialogTitle>Delete Task?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#64748b' }}>
                        Are you sure you want to delete the task: <strong>{dialogs.taskToDelete?.task_title}</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogs(p => ({ ...p, taskToDelete: null }))} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, textTransform: 'none' }}
                        onClick={() => handleDeleteAction('SubTask')}
                    >
                        {dialogs.isTaskDeleting ? (
                            <Loader size={18} color="inherit" />
                        ) : (
                            'Delete Task'
                        )}
                    </Button>
                </DialogActions>
            </AppDialog>

            {/* CREATE TASK DIALOG */}
            <AppDialog open={dialogs.createTask} onClose={() => setDialogs(p => ({ ...p, createTask: false }))} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Create New Task
                    <IconButton onClick={() => setDialogs(p => ({ ...p, createTask: false }))}><Close /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <CreateTask
                        storyData={state.data}
                        onClose={() => {
                            setDialogs(p => ({ ...p, createTask: false }));
                            showSnackbar("Task created successfully");
                            fetchDetails();
                        }}
                        mode='getStory'
                    />
                </DialogContent>
            </AppDialog>

            {/* GLOBAL SNACKBAR */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(p => ({ ...p, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%', minWidth: '300px' }}>{snackbar.message}</Alert>
            </Snackbar>
        </>
    );
}

export default UserStoryDetails;